import React, { FC, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card, CardContent,
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@pisaka/ui';
import {
  PenLine, ScanText, Sparkles, ALargeSmall,
  ArrowRight, Menu, X, Users, User,
} from 'lucide-react';
import { AppStateTypes } from '../../store/appStateTypes';
import { useStats } from '../stats/useStats';
import { FooterComponent } from '../../core/components/footer/FooterComponent';
import { useModal } from '../../hooks/useModal';
import { MODAL_NAME_AUTH } from '../../constants/modalNamesConstants';
import AuthModalContainer from '../auth/AuthModalContainer';
import logoImg from '../../core/components/header/logo.svg';
import { LanguageSwitcher } from '../../core/components/LanguageSwitcher';

// ── Animated counter hook ──────────────────────────────────────────────────
function useCountUp(target: number, duration = 1800) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!target) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const startTime = performance.now();
        const tick = (now: number) => {
          const progress = Math.min((now - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
}

function formatCount(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1000) return `${Math.floor(n / 1000)}K`;
  return n.toLocaleString('ru');
}

// ── Stat item ─────────────────────────────────────────────────────────────
const StatItem: FC<{ icon: React.ReactNode; value: number; suffix?: string; label: string }> = ({ icon, value, suffix = '', label }) => {
  const { count, ref } = useCountUp(value);
  return (
    <div ref={ref} className="flex flex-col items-center gap-2">
      <div className="text-accent mb-1">{icon}</div>
      <div className="text-5xl font-black text-gray-900 tabular-nums">
        {formatCount(count)}{suffix}
      </div>
      <div className="text-sm text-gray-500 font-medium">{label}</div>
    </div>
  );
};

// ── Data ──────────────────────────────────────────────────────────────────
const TOOLS_META = [
  { href: '/editor/', icon: PenLine, color: 'text-blue-500', bg: 'bg-blue-50', badge: null, key: 'editor' },
  { href: '/recognize/', icon: ScanText, color: 'text-orange-500', bg: 'bg-orange-50', badge: 'new', key: 'recognize' },
  { href: '/ai/', icon: Sparkles, color: 'text-purple-500', bg: 'bg-purple-50', badge: 'new', key: 'ai' },
  { href: '/font-creator/', icon: ALargeSmall, color: 'text-green-500', bg: 'bg-green-50', badge: null, key: 'font' },
];

const FAQ_KEYS = [0, 1, 2, 3, 4];

// ── Navbar ─────────────────────────────────────────────────────────────────
const Navbar: FC = () => {
  const { user } = useSelector((s: AppStateTypes) => s.auth);
  const { onOpenModal } = useModal(MODAL_NAME_AUTH);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();

  const navLinks: [string, string][] = [
    [t('landing.nav.notes'), '/editor/'],
    [t('landing.nav.recognition'), '/recognize/'],
    [t('sidebar.aiChat'), '/ai/'],
    [t('landing.nav.fonts'), '/font-creator/'],
    [t('landing.nav.pricing'), '/pricing/'],
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1B1C38]/95 backdrop-blur-sm border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 h-[60px] flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5">
          <img src={logoImg} alt="Pisaka" className="w-8 h-8" />
          <span className="text-white font-bold text-lg tracking-tight">{t('brand')}</span>
        </a>

        <div className="hidden md:flex items-center gap-6 text-sm text-white/70">
          {navLinks.map(([label, href]) => (
            <a key={href} href={href} className="hover:text-white transition-colors">{label}</a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher variant="dark" />
          {user ? (
            <a href="/profile/" className="flex items-center gap-1.5 text-sm font-medium text-white hover:text-white/80 transition-colors max-w-[140px]">
              <User className="w-4 h-4 shrink-0" />
              <span className="truncate">{user.name || user.email}</span>
            </a>
          ) : (
            <Button
              size="sm"
              onClick={() => onOpenModal()}
              className="bg-gradient-accent hover:bg-accent shadow-accent text-white border-0"
            >
              {t('landing.nav.login')}
            </Button>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setMenuOpen(v => !v)}>
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-[#1B1C38] border-t border-white/10 px-4 py-4 flex flex-col gap-3">
          {navLinks.map(([label, href]) => (
            <a key={href} href={href} className="text-white/70 hover:text-white text-sm py-1">{label}</a>
          ))}
          <div className="pt-2 border-t border-white/10 flex items-center justify-between">
            <Button size="sm" onClick={() => onOpenModal()} className="bg-gradient-accent hover:bg-accent text-white border-0">
              {t('landing.nav.login')}
            </Button>
            <LanguageSwitcher variant="dark" />
          </div>
        </div>
      )}
    </nav>
  );
};

// ── Main component ────────────────────────────────────────────────────────
export const LandingPage: FC = () => {
  const { fontsCounter, usersCounter, tokensSpent } = useSelector((s: AppStateTypes) => s.stats);
  const { loadStatsData } = useStats();
  const { onOpenModal } = useModal(MODAL_NAME_AUTH);
  const { t } = useTranslation();

  useEffect(() => { loadStatsData(); }, []);

  return (
    <>
      <Helmet>
        <title>Писака | Бесплатный Генератор Конспектов</title>
        <meta name="description" content="Бесплатный генератор конспектов онлайн: рукописный или печатный. Текст, фото, аудио, PDF и YouTube — в конспект. Pisaka AI, распознавание текста и генератор шрифтов." />
        <link rel="canonical" href="https://pisaka.online/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pisaka.online/" />
        <meta property="og:title" content="Писака — Умный помощник для учёбы" />
        <meta property="og:description" content="Конспекты от руки, распознавание текста, Pisaka AI и генератор шрифтов — всё бесплатно в одном месте." />
        <meta property="og:site_name" content="Pisaka" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Писака — Умный помощник для учёбы" />
        <meta name="twitter:description" content="Конспекты от руки, распознавание текста, Pisaka AI и генератор шрифтов — всё бесплатно в одном месте." />
      </Helmet>

      <Navbar />
      <AuthModalContainer />

      {/* Hero */}
      <section className="bg-[#1B1C38] pt-[60px]">
        <div className="max-w-4xl mx-auto px-4 py-28 text-center">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6 leading-[1.1] tracking-tight">
            <span className="text-accent">{t('landing.hero.titleBrand')}</span> {t('landing.hero.titleLine1')}<br />{t('landing.hero.titleLine2')}
          </h1>
          <p className="text-lg md:text-xl text-white/60 mb-10 max-w-xl mx-auto leading-relaxed">
            {t('landing.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/editor/" className="inline-flex items-center justify-center gap-2 py-3 px-8 bg-gradient-accent hover:bg-accent shadow-accent text-white rounded-xl font-semibold text-sm transition-colors">
              {t('landing.hero.cta')} <ArrowRight className="w-4 h-4" />
            </a>
            <button
              onClick={() => document.getElementById('tools')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 py-3 px-8 border border-white/20 text-white/80 hover:text-white hover:border-white/40 rounded-xl font-semibold text-sm transition-colors"
            >
              {t('landing.hero.learnMore')}
            </button>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section id="tools" className="bg-white py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-gray-900">{t('landing.tools.sectionTitle')}</h2>
            <p className="text-gray-500 mt-3">{t('landing.tools.sectionSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {TOOLS_META.map(({ href, icon: Icon, color, bg, badge, key }) => (
              <a key={href} href={href} className="group block">
                <Card className="h-full border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 rounded-2xl overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`${bg} rounded-xl p-3 flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-gray-900 text-base">{t(`landing.tools.${key}.title`)}</h3>
                          {badge && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white uppercase">{badge}</span>}
                        </div>
                        <p className="text-sm text-gray-500 leading-relaxed">{t(`landing.tools.${key}.description`)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-50 py-16 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
            <StatItem icon={<Users className="w-7 h-7" />} value={usersCounter} label={t('landing.stats.users')} />
            <StatItem icon={<ALargeSmall className="w-7 h-7" />} value={fontsCounter} label={t('landing.stats.fonts')} />
            <StatItem icon={<Sparkles className="w-7 h-7" />} value={tokensSpent} label={t('landing.stats.tokens')} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">{t('landing.faq.sectionTitle')}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_KEYS.map((i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-gray-200 rounded-xl px-5 overflow-hidden">
                <AccordionTrigger className="text-sm font-semibold text-gray-800 hover:no-underline py-4 text-left">{t(`landing.faq.q${i}`)}</AccordionTrigger>
                <AccordionContent className="text-sm text-gray-500 leading-relaxed pb-4">{t(`landing.faq.a${i}`)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <FooterComponent />
    </>
  );
};
